import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useListItemRoles } from 'hooks/useListItemRoles/useListItemRoles';
import { isMQDesktop, isMQTablet, matchPrefersReducedMotion } from 'style/styled-components/cssMediaQueries';
import {
    ARROW_ANCHOR,
    ArrowLeft,
    ArrowRight,
    ArrowWrapper,
    LaneScroller,
    LaneWrapper,
    paddingDesktop,
    paddingMobile,
    paddingTablet,
} from './Lane.css';

type LaneProps = {
    containerExtraClassName?: string;
    scrollerExtraClassName?: string;
    arrowLeft: any;
    arrowRight: any;
    scrollLeft?: number;
    keepArrows?: boolean;
    showArrowsWhenActive?: boolean;
    renderNavigationArrows?: boolean;
    asList?: boolean;
    listLabelledBy?: string;
    navigateTo?: {
        number: number;
        smooth: boolean;
    };
    navigateByViewPort?: boolean;
    useSnap?: boolean;
    withoutPadding?: boolean;
    isEditorialPill?: boolean;
    isCardChannel?: boolean;
    scrollable?: boolean;
    onVisibleIndexesChanged?: (first: number, last: number) => void;
    onScrollRightEdgeReached?: () => void;
    onScrollLeftEdgeReached?: () => void;
    onScrollStopped?: (scrollPosition) => void;
    scrolledPercentage?: (percentage) => void;
    isMobile?: boolean;
    stripeType?: string;
    children?: React.ReactNode;
};

const SCROLL_STOP_TIMEOUT = 100;
const SCROLL_EDGE_TOLERANCE = 2;
const NAVIGATION_LOCK_TIMEOUT = 1000;
const ARROW_HEIGHT = 36;
const ARROW_WRAPPER_FALLBACK_TOP = 24;
const CLASS_NAME_ACTIVE = 'active';
const CLASS_NAME_PARTIAL = 'partial';
const CLASS_NAME_FIRST_PARTIAL = 'first-partial';
const CLASS_NAME_TO_LEFT = 'to-left';
const CLASS_NAME_TO_RIGHT = 'to-right';
const CLASS_NAME_SMOOTH = 'smooth';

const canScrollRight = (scroller: HTMLElement) => scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft > SCROLL_EDGE_TOLERANCE;

export const Lane: FC<LaneProps> = ({
    containerExtraClassName,
    scrollerExtraClassName,
    arrowLeft,
    arrowRight,
    scrollLeft = 0,
    keepArrows = false,
    showArrowsWhenActive = false,
    renderNavigationArrows = true,
    asList = false,
    listLabelledBy,
    navigateTo = null,
    navigateByViewPort = false,
    useSnap = false,
    withoutPadding = false,
    isEditorialPill = false,
    scrollable = false,
    isMobile = false,
    onVisibleIndexesChanged,
    onScrollStopped,
    scrolledPercentage,
    onScrollLeftEdgeReached,
    onScrollRightEdgeReached,
    children,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);

    const scrollTimeout = useRef(null);
    const navigationLockTimeout = useRef(null);
    const canNavigate = useRef(true);

    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [anchorCenterY, setAnchorCenterY] = useState<number | null>(null);
    const scrollPadding = useRef(paddingMobile);

    const childCount = React.Children.count(children);
    const isList = asList || !!listLabelledBy;

    useListItemRoles(scrollerRef, isList);

    const updateVisibleArea = () => {
        if (!scrollerRef?.current) {
            return;
        }

        scrollPadding.current = paddingMobile;

        if (isMQTablet()) {
            scrollPadding.current = paddingTablet;
        }

        if (isMQDesktop()) {
            scrollPadding.current = paddingDesktop;
        }

        if (withoutPadding) {
            scrollPadding.current = 2;
        }

        const visibleAreaStart = scrollerRef.current.scrollLeft;
        const visibleAreaEnd = visibleAreaStart + scrollerRef.current.clientWidth;

        let firstVisibleItemIndex = -1;
        let lastVisibleItemIndex = childCount;

        scrollerRef.current.childNodes.forEach((node, index) => {
            const divNode = node as HTMLDivElement;
            let isActive = false;

            divNode.classList.remove(CLASS_NAME_ACTIVE, CLASS_NAME_PARTIAL, CLASS_NAME_FIRST_PARTIAL);

            if (
                divNode.offsetLeft >= visibleAreaStart - SCROLL_EDGE_TOLERANCE &&
                divNode.offsetLeft + divNode.clientWidth < visibleAreaEnd + SCROLL_EDGE_TOLERANCE
            ) {
                isActive = true;
                divNode.classList.add(CLASS_NAME_ACTIVE);

                if (firstVisibleItemIndex === -1) {
                    firstVisibleItemIndex = index;
                }

                lastVisibleItemIndex = index;
            }

            if (!isActive && divNode.offsetLeft >= visibleAreaStart && divNode.offsetLeft <= visibleAreaEnd) {
                divNode.classList.add(CLASS_NAME_PARTIAL);
                if (index > lastVisibleItemIndex) {
                    lastVisibleItemIndex = index;
                }
            }

            if (divNode.offsetLeft < visibleAreaStart && divNode.offsetLeft + divNode.offsetWidth >= visibleAreaStart) {
                divNode.classList.add(CLASS_NAME_FIRST_PARTIAL);
                if (index < firstVisibleItemIndex) {
                    firstVisibleItemIndex = index;
                }
            }
        });

        if (onVisibleIndexesChanged) {
            onVisibleIndexesChanged(firstVisibleItemIndex, lastVisibleItemIndex);
        }

        if (scrollerRef?.current) {
            const scroller = scrollerRef.current;

            setShowLeftArrow(scroller.scrollLeft > (keepArrows ? 0 : scrollPadding.current));
            setShowRightArrow(canScrollRight(scroller));
        }
    };

    // Measures the first card's image area ([data-arrow-anchor]) and stores its vertical
    // center relative to LaneWrapper, so the arrows line up with the image regardless of card type/height.
    const measureAnchor = () => {
        const wrapper = wrapperRef.current;
        const anchor = scrollerRef.current?.querySelector<HTMLElement>(`[${ARROW_ANCHOR}]`);

        if (!wrapper || !anchor) {
            setAnchorCenterY(null);
            return;
        }

        const wrapperRect = wrapper.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();

        setAnchorCenterY(anchorRect.top - wrapperRect.top + anchorRect.height / 2);
    };

    const getLastPartiallyVisible = (): HTMLDivElement | null => {
        const partials = scrollerRef.current.getElementsByClassName(CLASS_NAME_PARTIAL);
        return partials?.length ? (partials.item(0) as HTMLDivElement) : null;
    };

    const getFirstPartiallyVisible = (): HTMLDivElement | null => {
        const partials = scrollerRef.current.getElementsByClassName(CLASS_NAME_FIRST_PARTIAL);
        return partials?.length ? (partials.item(0) as HTMLDivElement) : null;
    };

    const getFirstFullyVisible = (): HTMLDivElement | null => {
        const actives = scrollerRef.current.getElementsByClassName(CLASS_NAME_ACTIVE);
        return actives?.length ? (actives.item(0) as HTMLDivElement) : null;
    };

    const scrollTo = (index: number, smooth = true) => {
        // item count is usually smaller than index (episode number)
        const itemCount = scrollerRef.current.childNodes.length;

        if (index < 0 || index > itemCount - 1) {
            console.log(`Invalid navigation index. The index can be between 0 and ${itemCount - 1}`);
        }

        const targetItem = scrollerRef.current.childNodes.item(index) as HTMLDivElement;
        if (!targetItem) {
            return;
        }

        if (smooth) {
            scrollerRef.current.classList.add(CLASS_NAME_SMOOTH);
        }

        scrollerRef.current.scrollLeft = targetItem.offsetLeft - scrollPadding.current;
    };

    const scroll = (to: number) => {
        const scroller = scrollerRef.current;

        if (!canNavigate.current || !scroller) {
            return;
        }

        const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
        const distance = Math.min(Math.max(scroller.scrollLeft + to, 0), maxScrollLeft) - scroller.scrollLeft;

        if (Math.abs(distance) <= SCROLL_EDGE_TOLERANCE) {
            updateVisibleArea();
            return;
        }

        canNavigate.current = false;

        if (navigationLockTimeout.current) {
            clearTimeout(navigationLockTimeout.current);
        }
        navigationLockTimeout.current = window.setTimeout(() => {
            canNavigate.current = true;
        }, NAVIGATION_LOCK_TIMEOUT);

        scroller.scrollBy({ left: distance, behavior: matchPrefersReducedMotion() ? 'auto' : 'smooth' });
    };

    const getRightScrollWidth = () => {
        // try to find the first not fully visible item
        const match: HTMLDivElement = !navigateByViewPort && getLastPartiallyVisible();

        if (match) {
            if (useSnap) {
                return match.offsetLeft - scrollerRef.current.scrollLeft;
            }

            return match.offsetLeft - scrollerRef.current.scrollLeft - scrollPadding.current;
        }

        // in case there is no match scroll with view-port width
        const viewPortWidth = wrapperRef.current.clientWidth;
        return viewPortWidth - scrollPadding.current;
    };

    const onRightClicked = () => {
        scrollerRef.current.classList.add(CLASS_NAME_TO_RIGHT);
        scroll(getRightScrollWidth());
    };

    const onScrollStop = () => {
        if (!scrollerRef.current) return;

        if (onScrollStopped) {
            onScrollStopped(scrollerRef.current.scrollLeft);
        }

        canNavigate.current = true;

        if (navigationLockTimeout.current) {
            clearTimeout(navigationLockTimeout.current);
            navigationLockTimeout.current = null;
        }

        scrollerRef.current.classList.remove(CLASS_NAME_TO_LEFT, CLASS_NAME_TO_RIGHT, CLASS_NAME_SMOOTH);

        updateVisibleArea();

        if (scrollerRef?.current) {
            const scroller = scrollerRef.current;

            if (scroller.scrollLeft <= scrollPadding.current + SCROLL_EDGE_TOLERANCE && onScrollLeftEdgeReached) {
                onScrollLeftEdgeReached();
            }

            if (!canScrollRight(scroller) && onScrollRightEdgeReached) {
                onScrollRightEdgeReached();
            }

            if (scrolledPercentage) {
                scrolledPercentage((scroller.scrollLeft * 100) / (scroller.scrollWidth - scroller.clientWidth));
            }
        }
    };

    const onScroll = () => {
        updateVisibleArea();

        if (scrollTimeout.current) {
            clearTimeout(scrollTimeout.current);
            scrollTimeout.current = null;
        }

        scrollTimeout.current = window.setTimeout(() => {
            onScrollStop();
        }, SCROLL_STOP_TIMEOUT);
    };

    const getLeftScrollWidth = () => {
        const viewPortWidth = wrapperRef.current.clientWidth;
        const scroller = scrollerRef.current;

        if (useSnap) {
            // try to find the first not fully visible item on the left
            const firstPartial = !navigateByViewPort && getFirstPartiallyVisible();
            if (firstPartial) {
                return -Math.min(
                    scroller.clientWidth + scroller.scrollLeft - firstPartial.offsetLeft - firstPartial.clientWidth,
                    viewPortWidth + scrollPadding.current
                );
            }

            // try to find the first not fully visible item on the right
            const match: HTMLDivElement = !navigateByViewPort && getLastPartiallyVisible();

            if (match) {
                return scroller.scrollLeft - match.offsetLeft;
            }

            return -viewPortWidth + scrollPadding.current;
        }

        const firstActive = getFirstFullyVisible();

        if (!firstActive) {
            return -viewPortWidth + scrollPadding.current;
        }

        const maximumScrollLength = viewPortWidth - 2 * scrollPadding.current;

        // find the active index
        let activeIndex = scroller.children.length;
        for (let i = scroller.children.length; i > 0; i -= 1) {
            if (scroller.children.item(i) === firstActive) {
                activeIndex = i;
                break;
            }
        }

        // find the last item which fits
        let helper = firstActive.offsetWidth;
        let lastItemFulfill: HTMLDivElement = null;
        for (let i = activeIndex; i > 0; i -= 1) {
            const previousItem = scroller.children.item(i - 1) as HTMLDivElement;

            if (helper + previousItem.offsetWidth < maximumScrollLength) {
                helper += previousItem.offsetWidth;
                lastItemFulfill = previousItem;
            } else {
                break;
            }
        }

        return -(scroller.scrollLeft - (lastItemFulfill?.offsetLeft ?? 0) + scrollPadding.current);
    };

    const onLeftClicked = () => {
        scrollerRef.current.classList.add(CLASS_NAME_TO_LEFT);
        scroll(getLeftScrollWidth());
    };

    const onMouseEnter = () => {
        if (hovered) {
            return;
        }
        setHovered(true);
    };

    const onMouseLeave = () => {
        if (!hovered) {
            return;
        }
        setHovered(false);
    };

    useEffect(() => {
        scrollerRef.current.scrollLeft = scrollLeft;
    }, [scrollLeft]);

    useEffect(() => {
        if (navigateTo != null) {
            scrollTo(navigateTo.number, navigateTo.smooth);
        }
    }, [navigateTo]);

    useEffect(() => {
        if (childCount < 2) {
            return;
        }

        if (hovered) {
            measureAnchor();
        }
    }, [hovered]);

    useEffect(() => {
        const scroller = scrollerRef.current;
        setShowRightArrow(canScrollRight(scroller));

        updateVisibleArea();
        measureAnchor();
    }, [children]);

    // Position the arrows on the card image-area center.
    useLayoutEffect(() => {
        measureAnchor();
    }, []);

    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        const scroller = scrollerRef.current;

        if (!wrapper || !scroller) {
            return undefined;
        }

        const anchor = scroller.querySelector<HTMLElement>(`[${ARROW_ANCHOR}]`);

        let observer: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => measureAnchor());
            observer.observe(wrapper);
            if (anchor) {
                observer.observe(anchor);
            }
        }

        const onImageLoad = () => measureAnchor();
        scroller.addEventListener('load', onImageLoad, true);

        return () => {
            observer?.disconnect();
            scroller.removeEventListener('load', onImageLoad, true);
        };
    }, [childCount]);

    useEffect(() => {
        const onResize = () => {
            updateVisibleArea();
            measureAnchor();
        };

        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);

            if (navigationLockTimeout.current) {
                clearTimeout(navigationLockTimeout.current);
            }
        };
    }, []);

    let renderLeftArrow = ((showLeftArrow && renderNavigationArrows) || keepArrows) && childCount > 1 && !isMobile;
    let renderRightArrow = ((showRightArrow && renderNavigationArrows) || keepArrows) && childCount > 1 && !isMobile;

    if (!showLeftArrow && !showRightArrow) {
        renderLeftArrow = false;
        renderRightArrow = false;
    }

    const arrowWrapperTop = (anchorCenterY != null ? anchorCenterY : ARROW_WRAPPER_FALLBACK_TOP) - ARROW_HEIGHT / 2;

    return (
        <LaneWrapper className={containerExtraClassName || ''} ref={wrapperRef} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <LaneScroller
                useSnap={useSnap || isMobile}
                scrollable={scrollable || isMobile}
                isEditorialPill={isEditorialPill}
                className={scrollerExtraClassName || ''}
                ref={scrollerRef}
                onScroll={onScroll}
                role={isList ? 'list' : undefined}
                aria-labelledby={listLabelledBy}
            >
                {children}
            </LaneScroller>
            {renderLeftArrow || renderRightArrow ? (
                <ArrowWrapper style={{ top: arrowWrapperTop }}>
                    {renderLeftArrow && (
                        <ArrowLeft
                            alwaysOn={keepArrows || (showArrowsWhenActive && showLeftArrow)}
                            active={showLeftArrow}
                            isEditorialPill={isEditorialPill}
                            onClick={() => {
                                if (!showLeftArrow) {
                                    return;
                                }
                                onLeftClicked();
                            }}
                        >
                            {arrowLeft}
                        </ArrowLeft>
                    )}
                    {renderRightArrow && (
                        <ArrowRight
                            alwaysOn={keepArrows || (showArrowsWhenActive && showRightArrow)}
                            active={showRightArrow}
                            isEditorialPill={isEditorialPill}
                            onClick={() => {
                                if (!showRightArrow) {
                                    return;
                                }
                                onRightClicked();
                            }}
                        >
                            {arrowRight}
                        </ArrowRight>
                    )}
                </ArrowWrapper>
            ) : null}
        </LaneWrapper>
    );
};
